import type { Point, Polygone, Projet } from '../../types/map';
import { DEFAULT_MARKER_STYLE } from '../../constants/map';

/**
 * Validate and normalize point data from various API formats
 * Uses 'unknown' with runtime type checks for external API data safety
 */
export const validatePoint = (rawPoint: unknown): Point | null => {
  if (!rawPoint || typeof rawPoint !== 'object') {
    return null;
  }

  const raw = rawPoint as Record<string, unknown>;
  let id: number;
  let properties: Record<string, unknown> = {};
  let latitude: number | undefined;
  let longitude: number | undefined;

  // Handle different GeoJSON structures
  if (raw.type === 'Feature' && raw.properties) {
    properties = raw.properties as Record<string, unknown>;
    id = (properties.id || raw.id) as number;
    const geometry = raw.geometry as Record<string, unknown> | null | undefined;

    if (geometry && geometry.type === 'Point' && Array.isArray(geometry.coordinates)) {
      longitude = parseFloat(geometry.coordinates[0] as string);
      latitude = parseFloat(geometry.coordinates[1] as string);
    } else if (!geometry || geometry.coordinates == null) {
      // Point global (SPEC §2.3) : pas de géométrie, valide quand même (absent de la carte, présent dans les résultats).
      latitude = undefined;
      longitude = undefined;
    } else {
      return null;
    }
  } else if (raw.properties && typeof raw.properties === 'object') {
    properties = raw.properties as Record<string, unknown>;
    id = (properties.id || raw.id) as number;
    const coordonnees = raw.coordonnees as Record<string, unknown> | undefined;
    
    if (coordonnees?.type === 'Point' && Array.isArray(coordonnees.coordinates)) {
      longitude = parseFloat(coordonnees.coordinates[0] as string);
      latitude = parseFloat(coordonnees.coordinates[1] as string);
    } else if (typeof raw.latitude === 'number' && typeof raw.longitude === 'number') {
      latitude = raw.latitude;
      longitude = raw.longitude;
    } else if (typeof properties.latitude === 'number' && typeof properties.longitude === 'number') {
      latitude = properties.latitude;
      longitude = properties.longitude;
    } else if (raw.coordonnees === null || properties.coordonnees === null) {
      // Point global (SPEC §2.3) : pas de géométrie, valide quand même.
      latitude = undefined;
      longitude = undefined;
    } else {
      return null;
    }
  } else {
    properties = raw as Record<string, unknown>;
    id = raw.id as number;
    const coordonnees = raw.coordonnees as Record<string, unknown> | undefined;
    
    if (coordonnees?.type === 'Point' && Array.isArray(coordonnees.coordinates)) {
      longitude = parseFloat(coordonnees.coordinates[0] as string);
      latitude = parseFloat(coordonnees.coordinates[1] as string);
    } else if (typeof raw.latitude === 'number' && typeof raw.longitude === 'number') {
      latitude = raw.latitude;
      longitude = raw.longitude;
    } else {
      return null;
    }
  }

  // Validate ID
  if (typeof id !== 'number') {
    id = parseInt(id);
  }
  if (isNaN(id)) {
    return null;
  }
  // Un point global (SPEC §2.3) n'a pas de coordonnées : latitude/longitude restent undefined.
  const isGlobal = latitude === undefined && longitude === undefined;
  if (!isGlobal) {
    if (latitude === undefined || longitude === undefined || isNaN(latitude) || isNaN(longitude)) {
      return null;
    }
    // Validate coordinates range
    if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      return null;
    }
  }

  const nom = (properties.nom || properties.name || '') as string;
  const titre = (properties.titre || properties.title || nom) as string;
  
  // Parse styleIcone
  let styleIcone = properties.styleIcone || properties.style || raw.styleIcone;
  if (typeof styleIcone === 'string') {
    try {
      styleIcone = JSON.parse(styleIcone);
    } catch (e) {
      styleIcone = null;
    }
  }

  if (!styleIcone || typeof styleIcone !== 'object') {
    styleIcone = { ...DEFAULT_MARKER_STYLE };
  }

  return {
    id,
    nom,
    titre,
    description: (properties.description || '') as string,
    latitude,
    longitude,
    categorie: (properties.categorie || properties.category || 'general') as string,
    categorieId: (properties.categorieId || raw.categorieId) as number | undefined,
    styleIcone: styleIcone as Point['styleIcone'],
    visible: properties.visible !== false,
    baseMapIds: (Array.isArray(properties.baseMapIds) ? properties.baseMapIds : []) as string[],
    donneesPersonnalisees: (properties.donneesPersonnalisees || raw.donneesPersonnalisees || undefined) as Record<string, unknown> | undefined,
    categorieMarqueur: (properties.categorieMarqueur || raw.categorieMarqueur || undefined) as Point['categorieMarqueur'],
    projetId: (properties.projetId ?? raw.projetId) as number | undefined,
  };
};

/**
 * Validate and normalize a Projet's own base fields (sans ses emplacements,
 * attachés séparément par l'appelant via un regroupement des Points validés).
 */
export const validateProjet = (rawProjet: unknown): Omit<Projet, 'points'> | null => {
  if (!rawProjet || typeof rawProjet !== 'object') {
    return null;
  }

  const raw = rawProjet as Record<string, unknown>;
  let id = raw.id as number;
  if (typeof id !== 'number') {
    id = parseInt(id as unknown as string, 10);
  }
  if (isNaN(id)) {
    return null;
  }

  const nom = (raw.nom || `Projet ${id}`) as string;
  const titre = (raw.titre || nom) as string;

  return {
    id,
    nom,
    titre,
    description: (raw.description || undefined) as string | undefined,
    categorieId: (raw.categorieId ?? undefined) as number | undefined,
    categorieMarqueur: (raw.categorieMarqueur || undefined) as Projet['categorieMarqueur'],
    donneesPersonnalisees: (raw.donneesPersonnalisees || undefined) as Record<string, unknown> | undefined,
    visible: raw.visible !== false,
    baseMapIds: (Array.isArray(raw.baseMapIds) ? raw.baseMapIds : []) as string[],
    ordreAffichage: (raw.ordreAffichage as number | undefined) ?? undefined,
  };
};

/**
 * Validate and normalize polygon data from various API formats
 * Uses 'unknown' with runtime type checks for external API data safety
 */
export const validatePolygone = (rawPolygone: unknown): Polygone | null => {
  if (!rawPolygone || typeof rawPolygone !== 'object') {
    return null;
  }

  const raw = rawPolygone as Record<string, unknown>;
  let id: number;
  let properties: Record<string, unknown> = {};
  let coordinates: number[][][];

  // Handle different GeoJSON structures for polygons
  if (raw.type === 'Feature' && raw.geometry && raw.properties) {
    properties = raw.properties as Record<string, unknown>;
    id = (properties.id || raw.id) as number;
    const geometry = raw.geometry as Record<string, unknown>;
    
    if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
      coordinates = geometry.coordinates as number[][][];
    } else {
      return null;
    }
  } else if (raw.properties && typeof raw.properties === 'object') {
    properties = raw.properties as Record<string, unknown>;
    id = (properties.id || raw.id) as number;
    const coordonnees = raw.coordonnees as Record<string, unknown> | undefined;
    
    if (coordonnees?.type === 'Polygon' && Array.isArray(coordonnees.coordinates)) {
      coordinates = coordonnees.coordinates as number[][][];
    } else {
      return null;
    }
  } else {
    properties = raw as Record<string, unknown>;
    id = raw.id as number;
    const coordonnees = raw.coordonnees as Record<string, unknown> | undefined;
    
    if (coordonnees?.type === 'Polygon' && Array.isArray(coordonnees.coordinates)) {
      coordinates = coordonnees.coordinates as number[][][];
    } else {
      return null;
    }
  }

  // Validate ID
  if (typeof id !== 'number') {
    id = parseInt(id);
  }
  if (isNaN(id)) {
    return null;
  }

  const nom = (properties.nom || properties.name || `Polygone ${id}`) as string;
  const titre = (properties.titre || properties.title || nom) as string;
  
  // Parse style
  let style = properties.style || raw.style;
  if (typeof style === 'string') {
    try {
      style = JSON.parse(style);
    } catch (e) {
      style = null;
    }
  }

  if (!style || typeof style !== 'object') {
    style = {
      couleurFond: 'rgba(76, 175, 80, 0.3)',
      couleurBordure: '#4CAF50',
      largeurBordure: 2
    };
  }

  const validatedPolygone: Polygone = {
    id,
    nom,
    titre,
    description: (properties.description || '') as string,
    coordonnees: {
      type: 'Polygon',
      coordinates
    },
    categorie: (properties.categorie || properties.category || 'general') as string,
    style: style as Polygone['style'],
    visible: properties.visible !== false,
    baseMapIds: (Array.isArray(properties.baseMapIds) ? properties.baseMapIds : []) as string[],
  };

  return validatedPolygone;
};
